<template>
  <div class="training-section-wrapper">
    <home-section-header :title="displayTitle" :icon="icon" :to="routeLink" />

    <!-- Content -->
    <div class="training-section-content" v-if="hasTrainings">
      <!-- Slider Layout -->
      <template v-if="useSlider">
        <div class="uk-slider-container-offset" uk-slider="finite: true">
          <div class="uk-position-relative uk-visible-toggle">
            <ul class="uk-slider-items training-slider-grid">
              <li v-for="(training, index) in trainings" :key="keyPrefix + index">
                <training-card
                  :training="training"
                  :cardType="cardType"
                  :rating_star_points="ratingStarPoints"
                  :star_increment="starIncrement"
                  @provider-click="onProviderClick"
                />
              </li>
            </ul>
            <!-- Slider Buttons -->
            <a class="uk-position-center-left text-primary uk-position-small uk-hidden-hover slider-nav-btn" href="#"
               uk-slidenav-previous uk-slider-item="previous"></a>
            <a class="uk-position-center-right text-primary uk-position-small uk-hidden-hover slider-nav-btn" href="#"
               uk-slidenav-next uk-slider-item="next"></a>
          </div>
          <!-- Slider Dot Nav Buttons -->
          <ul class="uk-slider-nav uk-dotnav uk-flex-center uk-margin"></ul>
        </div>
      </template>

      <!-- Grid Layout -->
      <template v-else>
        <div class="training-grid">
          <div v-for="(training, index) in trainings" :key="keyPrefix + index" class="training-grid-item">
            <training-card
              :training="training"
              :cardType="cardType"
              :rating_star_points="ratingStarPoints"
              :star_increment="starIncrement"
              @provider-click="onProviderClick"
            />
          </div>
        </div>
      </template>
    </div>
    <hr>
  </div>
</template>

<script>
import TrainingCard from "@/view/components/card/TrainingCard.vue";
import HomeSectionHeader from "@/view/components/section/HomeSectionHeader.vue";

export default {
  name: "TrainingSection",
  components: {
    HomeSectionHeader,
    TrainingCard
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    trainings: {
      type: Array,
      default: () => []
    },
    cardType: {
      type: String,
      default: 'assigned'
    },
    icon: {
      type: String,
      default: 'uil-puzzle-piece'
    },
    routeName: {
      type: String,
      default: 'user.my-assignment'
    },
    routeParams: {
      type: Object,
      default: () => ({})
    },
    useSlider: {
      type: Boolean,
      default: false
    },
    ratingStarPoints: {
      type: Array,
      default: () => []
    },
    starIncrement: {
      type: Number,
      default: 0
    },
    keyPrefix: {
      type: String,
      default: 'training_item_'
    }
  },
  computed: {
    displayTitle() {
      return this.getLocalizedWidgetTitle(this.item);
    },
    routeLink() {
      const link = { name: this.routeName };

      if (Object.keys(this.routeParams).length > 0) {
        link.params = this.routeParams;
      }

      // my-assignment tab parametresi olmadan açıldığında zaten "Tümü" sekmesi aktif gelir
      // Admin tarafından eklenen widget -> widget_id ile git
      if (this.item?.widgetable_type && this.item?.id) {
        link.query = { widget_id: this.item.id };
      }
      // Sistem widget'ı ve my-assignment route'u -> widget_type ile git
      else if (this.routeName === 'user.my-assignment' && this.item?.type) {
        link.query = { widget_type: this.item.type };
      }

      return link;
    },
    hasTrainings() {
      return this.trainings && this.trainings.length > 0;
    }
  },
  methods: {
    onProviderClick(data) {
      this.$emit('provider-click', data);
    }
  }
}
</script>

<style scoped>
/* Wrapper */
.training-section-wrapper {
  width: 100%;
}

.training-section-content {
  width: 100%;
  overflow: hidden;
  padding-bottom: 8px;
}

/* CSS Grid Layout */
.training-grid {
  display: grid !important;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.training-grid-item {
  width: 100% !important;
  min-width: 0;
  padding: 0 !important;
  margin: 0 !important;
}

/* Slider Grid */
.training-slider-grid {
  display: flex !important;
  gap: 12px;
  width: 100% !important;
  list-style: none;
  padding: 0 !important;
  margin: 0 !important;
}

.training-slider-grid > li {
  flex: 0 0 calc(25% - 9px);
  min-width: 180px;
  width: auto !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Large Desktop */
@media (min-width: 1200px) {
  .training-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .training-slider-grid > li {
    flex: 0 0 calc(25% - 12px);
  }
}

/* Desktop */
@media (max-width: 1199px) and (min-width: 961px) {
  .training-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .training-slider-grid > li {
    flex: 0 0 calc(25% - 9px);
  }
}

/* Tablet - 3 columns */
@media (max-width: 960px) {
  .training-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .training-slider-grid > li {
    flex: 0 0 calc(33.333% - 8px);
    min-width: 160px;
  }
}

/* Small Tablet - 2 columns */
@media (max-width: 768px) {
  .training-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .training-slider-grid {
    gap: 10px;
  }

  .training-slider-grid > li {
    flex: 0 0 calc(50% - 5px);
    min-width: 140px;
  }
}

/* Mobile - 2 columns */
@media (max-width: 640px) {
  .training-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .training-slider-grid {
    gap: 8px;
  }

  .training-slider-grid > li {
    flex: 0 0 calc(50% - 4px);
    min-width: 130px;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .training-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .training-slider-grid {
    gap: 6px;
  }

  .training-slider-grid > li {
    flex: 0 0 calc(50% - 3px);
    min-width: 110px;
  }
}

/* Slider Nav Buttons - touch-friendly */
.slider-nav-btn {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

@media (max-width: 960px) {
  .slider-nav-btn {
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Slider Dot Nav - touch-friendly */
@media (max-width: 960px) {
  .uk-dotnav > * > * {
    min-width: 12px;
    min-height: 12px;
    touch-action: manipulation;
  }
}
</style>
